import React, { useState } from 'react';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import Select from '../../../components/ui/Select';
import Icon from '../../../components/AppIcon';

const CustomerRegistrationModal = ({ isOpen, onClose, agentId }) => {
  const [currentStep, setCurrentStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState({});
  
  const [formData, setFormData] = useState({
    // Personal Information
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    dateOfBirth: '',
    
    // Address Information
    street: '',
    city: '',
    state: '',
    postalCode: '',
    country: '',
    
    // Identity Information
    idType: '',
    idNumber: '',
    nationality: '',
    
    // Additional Information
    occupation: '',
    sourceOfFunds: '',
    purposeOfAccount: ''
  });

  const steps = [
    { number: 1, title: 'Personal Info', icon: 'User' },
    { number: 2, title: 'Address', icon: 'MapPin' },
    { number: 3, title: 'Identity', icon: 'Shield' },
    { number: 4, title: 'Review', icon: 'CheckCircle' }
  ];

  const idTypes = [
    { value: 'passport', label: 'Passport' },
    { value: 'drivers_license', label: "Driver\'s License" },
    { value: 'national_id', label: 'National ID Card' },
    { value: 'state_id', label: 'State ID' }
  ];

  const countries = [
    { value: 'us', label: 'United States' },
    { value: 'uk', label: 'United Kingdom' },
    { value: 'ca', label: 'Canada' },
    { value: 'mx', label: 'Mexico' },
    { value: 'in', label: 'India' },
    { value: 'ph', label: 'Philippines' }
  ];

  const sourceOfFundsOptions = [
    { value: 'employment', label: 'Employment/Salary' },
    { value: 'business', label: 'Business Income' },
    { value: 'investments', label: 'Investments' },
    { value: 'savings', label: 'Savings' },
    { value: 'retirement', label: 'Retirement/Pension' },
    { value: 'other', label: 'Other' }
  ];

  const purposeOptions = [
    { value: 'family_support', label: 'Family Support' },
    { value: 'education', label: 'Education Fees' },
    { value: 'business', label: 'Business Payments' },
    { value: 'property', label: 'Property Purchase' },
    { value: 'medical', label: 'Medical Expenses' },
    { value: 'other', label: 'Other' }
  ];

  const handleInputChange = (e) => {
    const { name, value } = e?.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    // Clear error for this field
    if (errors?.[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  const validateStep = (step) => {
    const newErrors = {};
    
    if (step === 1) {
      if (!formData?.firstName?.trim()) newErrors.firstName = 'First name is required';
      if (!formData?.lastName?.trim()) newErrors.lastName = 'Last name is required';
      if (!formData?.email?.trim()) {
        newErrors.email = 'Email is required';
      } else if (!/\S+@\S+\.\S+/?.test(formData?.email)) {
        newErrors.email = 'Invalid email format';
      }
      if (!formData?.phone?.trim()) newErrors.phone = 'Phone number is required';
      if (!formData?.dateOfBirth) newErrors.dateOfBirth = 'Date of birth is required';
    }
    
    if (step === 2) {
      if (!formData?.street?.trim()) newErrors.street = 'Street address is required';
      if (!formData?.city?.trim()) newErrors.city = 'City is required';
      if (!formData?.state?.trim()) newErrors.state = 'State/Province is required';
      if (!formData?.postalCode?.trim()) newErrors.postalCode = 'Postal code is required';
      if (!formData?.country) newErrors.country = 'Country is required';
    }
    
    if (step === 3) {
      if (!formData?.idType) newErrors.idType = 'ID type is required';
      if (!formData?.idNumber?.trim()) newErrors.idNumber = 'ID number is required';
      if (!formData?.nationality) newErrors.nationality = 'Nationality is required';
      if (!formData?.occupation?.trim()) newErrors.occupation = 'Occupation is required';
      if (!formData?.sourceOfFunds) newErrors.sourceOfFunds = 'Source of funds is required';
      if (!formData?.purposeOfAccount) newErrors.purposeOfAccount = 'Purpose of account is required';
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors)?.length === 0;
  };

  const handleNext = () => {
    if (validateStep(currentStep)) {
      setCurrentStep(prev => Math.min(prev + 1, 4));
    }
  };

  const handleBack = () => {
    setCurrentStep(prev => Math.max(prev - 1, 1));
  };

  const handleSubmit = async () => {
    setLoading(true);
    
    try {
      // Simulate API call
      await new Promise(resolve => setTimeout(resolve, 2000));
      
      console.log('Registering customer:', {
        ...formData,
        agentId,
        registeredAt: new Date()?.toISOString()
      });
      
      // Show success and close
      alert('Customer registered successfully! Verification documents can be uploaded later.');
      onClose();
      
      // Reset form
      setFormData({
        firstName: '',
        lastName: '',
        email: '',
        phone: '',
        dateOfBirth: '',
        street: '',
        city: '',
        state: '',
        postalCode: '',
        country: '',
        idType: '',
        idNumber: '',
        nationality: '',
        occupation: '',
        sourceOfFunds: '',
        purposeOfAccount: ''
      });
      setCurrentStep(1);
    } catch (error) {
      console.error('Registration error:', error);
      alert('Failed to register customer. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      {/* Backdrop */}
      <div 
        className="fixed inset-0 bg-black bg-opacity-50 transition-opacity"
        onClick={onClose}
      />
      {/* Modal */}
      <div className="flex min-h-screen items-center justify-center p-4">
        <div className="relative bg-card rounded-lg shadow-xl max-w-4xl w-full max-h-[90vh] overflow-hidden">
          {/* Header */}
          <div className="sticky top-0 bg-card border-b border-border px-6 py-4 z-10">
            <div className="flex items-center justify-between">
              <div>
                <h2 className="text-xl font-semibold text-foreground">Register New Customer</h2>
                <p className="text-sm text-muted-foreground mt-1">
                  Step {currentStep} of {steps?.length}: {steps?.[currentStep - 1]?.title}
                </p>
              </div>
              <button
                onClick={onClose}
                className="text-muted-foreground hover:text-foreground transition-colors"
              >
                <Icon name="X" size={24} />
              </button>
            </div>

            {/* Progress Steps */}
            <div className="flex items-center justify-between mt-6">
              {steps?.map((step, index) => (
                <React.Fragment key={step?.number}>
                  <div className="flex flex-col items-center flex-1">
                    <div className={`
                      w-10 h-10 rounded-full flex items-center justify-center transition-colors
                      ${currentStep >= step?.number 
                        ? 'bg-primary text-white' :'bg-muted text-muted-foreground'
                      }
                    `}>
                      {currentStep > step?.number ? (
                        <Icon name="Check" size={20} />
                      ) : (
                        <Icon name={step?.icon} size={20} />
                      )}
                    </div>
                    <span className={`
                      text-xs mt-2 font-medium
                      ${currentStep >= step?.number ? 'text-primary' : 'text-muted-foreground'}
                    `}>
                      {step?.title}
                    </span>
                  </div>
                  {index < steps?.length - 1 && (
                    <div className={`
                      flex-1 h-0.5 mx-2 -mt-5 transition-colors
                      ${currentStep > step?.number ? 'bg-primary' : 'bg-border'}
                    `} />
                  )}
                </React.Fragment>
              ))}
            </div>
          </div>

          {/* Content */}
          <div className="px-6 py-6 overflow-y-auto max-h-[calc(90vh-280px)]">
            {/* Step 1: Personal Information */}
            {currentStep === 1 && (
              <div className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <Input
                    label="First Name"
                    name="firstName"
                    value={formData?.firstName}
                    onChange={handleInputChange}
                    error={errors?.firstName}
                    required
                  />
                  <Input
                    label="Last Name"
                    name="lastName"
                    value={formData?.lastName}
                    onChange={handleInputChange}
                    error={errors?.lastName}
                    required
                  />
                </div>

                <Input
                  label="Email Address"
                  name="email"
                  type="email"
                  value={formData?.email}
                  onChange={handleInputChange}
                  error={errors?.email}
                  required
                />

                <Input
                  label="Phone Number"
                  name="phone"
                  type="tel"
                  value={formData?.phone}
                  onChange={handleInputChange}
                  error={errors?.phone}
                  placeholder="+1 (555) 123-4567"
                  required
                />

                <Input
                  label="Date of Birth"
                  name="dateOfBirth"
                  type="date"
                  value={formData?.dateOfBirth}
                  onChange={handleInputChange}
                  error={errors?.dateOfBirth}
                  max={new Date()?.toISOString()?.split('T')?.[0]}
                  required
                />

                <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mt-4">
                  <div className="flex items-start space-x-3">
                    <Icon name="Info" size={20} className="text-blue-600 flex-shrink-0 mt-0.5" />
                    <div className="text-sm text-blue-900">
                      <p className="font-medium mb-1">Customer must be 18 or older</p>
                      <p>Ensure all information matches the customer's government-issued ID.</p>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Step 2: Address Information */}
            {currentStep === 2 && (
              <div className="space-y-4">
                <Input
                  label="Street Address"
                  name="street"
                  value={formData?.street}
                  onChange={handleInputChange}
                  error={errors?.street}
                  placeholder="123 Main Street, Apt 4B"
                  required
                />

                <div className="grid grid-cols-2 gap-4">
                  <Input
                    label="City"
                    name="city"
                    value={formData?.city}
                    onChange={handleInputChange}
                    error={errors?.city}
                    required
                  />
                  <Input
                    label="State/Province"
                    name="state"
                    value={formData?.state}
                    onChange={handleInputChange}
                    error={errors?.state}
                    required
                  />
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <Input
                    label="Postal/ZIP Code"
                    name="postalCode"
                    value={formData?.postalCode}
                    onChange={handleInputChange}
                    error={errors?.postalCode}
                    required
                  />
                  <Select
                    label="Country"
                    name="country"
                    value={formData?.country}
                    onChange={handleInputChange}
                    error={errors?.country}
                    options={countries}
                    required
                  />
                </div>

                <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 mt-4">
                  <div className="flex items-start space-x-3">
                    <Icon name="AlertTriangle" size={20} className="text-amber-600 flex-shrink-0 mt-0.5" />
                    <div className="text-sm text-amber-900">
                      <p className="font-medium mb-1">Address Verification</p>
                      <p>Customer may be required to provide proof of address during verification.</p>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Step 3: Identity & Compliance */}
            {currentStep === 3 && (
              <div className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <Select
                    label="ID Document Type"
                    name="idType"
                    value={formData?.idType}
                    onChange={handleInputChange}
                    error={errors?.idType}
                    options={idTypes}
                    required
                  />
                  <Input
                    label="ID Number"
                    name="idNumber"
                    value={formData?.idNumber}
                    onChange={handleInputChange}
                    error={errors?.idNumber}
                    required
                  />
                </div>

                <Select
                  label="Nationality"
                  name="nationality"
                  value={formData?.nationality}
                  onChange={handleInputChange}
                  error={errors?.nationality}
                  options={countries}
                  required
                />

                <Input
                  label="Occupation"
                  name="occupation"
                  value={formData?.occupation}
                  onChange={handleInputChange}
                  error={errors?.occupation}
                  placeholder="e.g., Software Engineer, Teacher, Business Owner"
                  required
                />

                <Select
                  label="Source of Funds"
                  name="sourceOfFunds"
                  value={formData?.sourceOfFunds}
                  onChange={handleInputChange}
                  error={errors?.sourceOfFunds}
                  options={sourceOfFundsOptions}
                  required
                />

                <Select
                  label="Purpose of Account"
                  name="purposeOfAccount"
                  value={formData?.purposeOfAccount}
                  onChange={handleInputChange}
                  error={errors?.purposeOfAccount}
                  options={purposeOptions}
                  required
                />

                <div className="bg-green-50 border border-green-200 rounded-lg p-4 mt-4">
                  <div className="flex items-start space-x-3">
                    <Icon name="Shield" size={20} className="text-green-600 flex-shrink-0 mt-0.5" />
                    <div className="text-sm text-green-900">
                      <p className="font-medium mb-1">Compliance Information</p>
                      <p>This information helps us comply with anti-money laundering (AML) regulations and protect against fraud.</p>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Step 4: Review */}
            {currentStep === 4 && (
              <div className="space-y-6">
                <div className="bg-muted/50 rounded-lg p-4">
                  <h3 className="font-semibold text-foreground mb-4 flex items-center">
                    <Icon name="User" size={18} className="mr-2" />
                    Personal Information
                  </h3>
                  <div className="grid grid-cols-2 gap-4 text-sm">
                    <div>
                      <p className="text-muted-foreground">Name</p>
                      <p className="font-medium text-foreground">{formData?.firstName} {formData?.lastName}</p>
                    </div>
                    <div>
                      <p className="text-muted-foreground">Date of Birth</p>
                      <p className="font-medium text-foreground">{formData?.dateOfBirth}</p>
                    </div>
                    <div>
                      <p className="text-muted-foreground">Email</p>
                      <p className="font-medium text-foreground">{formData?.email}</p>
                    </div>
                    <div>
                      <p className="text-muted-foreground">Phone</p>
                      <p className="font-medium text-foreground">{formData?.phone}</p>
                    </div>
                  </div>
                </div>

                <div className="bg-muted/50 rounded-lg p-4">
                  <h3 className="font-semibold text-foreground mb-4 flex items-center">
                    <Icon name="MapPin" size={18} className="mr-2" />
                    Address
                  </h3>
                  <div className="text-sm">
                    <p className="font-medium text-foreground">{formData?.street}</p>
                    <p className="text-muted-foreground">
                      {formData?.city}, {formData?.state} {formData?.postalCode}
                    </p>
                    <p className="text-muted-foreground">
                      {countries?.find(c => c?.value === formData?.country)?.label}
                    </p>
                  </div>
                </div>

                <div className="bg-muted/50 rounded-lg p-4">
                  <h3 className="font-semibold text-foreground mb-4 flex items-center">
                    <Icon name="Shield" size={18} className="mr-2" />
                    Identity & Compliance
                  </h3>
                  <div className="grid grid-cols-2 gap-4 text-sm">
                    <div>
                      <p className="text-muted-foreground">ID Type</p>
                      <p className="font-medium text-foreground">
                        {idTypes?.find(t => t?.value === formData?.idType)?.label}
                      </p>
                    </div>
                    <div>
                      <p className="text-muted-foreground">ID Number</p>
                      <p className="font-medium text-foreground">{formData?.idNumber}</p>
                    </div>
                    <div>
                      <p className="text-muted-foreground">Nationality</p>
                      <p className="font-medium text-foreground">
                        {countries?.find(c => c?.value === formData?.nationality)?.label}
                      </p>
                    </div>
                    <div>
                      <p className="text-muted-foreground">Occupation</p>
                      <p className="font-medium text-foreground">{formData?.occupation}</p>
                    </div>
                    <div>
                      <p className="text-muted-foreground">Source of Funds</p>
                      <p className="font-medium text-foreground">
                        {sourceOfFundsOptions?.find(s => s?.value === formData?.sourceOfFunds)?.label}
                      </p>
                    </div>
                    <div>
                      <p className="text-muted-foreground">Purpose</p>
                      <p className="font-medium text-foreground">
                        {purposeOptions?.find(p => p?.value === formData?.purposeOfAccount)?.label}
                      </p>
                    </div>
                  </div>
                </div>

                <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                  <div className="flex items-start space-x-3">
                    <Icon name="FileText" size={20} className="text-blue-600 flex-shrink-0 mt-0.5" />
                    <div className="text-sm text-blue-900">
                      <p className="font-medium mb-1">Next Steps</p>
                      <ul className="list-disc list-inside space-y-1">
                        <li>Customer will receive a verification email</li>
                        <li>They must upload ID documents within 7 days</li>
                        <li>Account will be activated after verification</li>
                        <li>You'll earn commission on their transactions</li>
                      </ul>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Footer */}
          <div className="sticky bottom-0 bg-card border-t border-border px-6 py-4">
            <div className="flex items-center justify-between">
              <Button
                variant="outline"
                onClick={currentStep === 1 ? onClose : handleBack}
                disabled={loading}
              >
                {currentStep === 1 ? 'Cancel' : 'Back'}
              </Button>

              <div className="flex items-center space-x-3">
                {currentStep < 4 ? (
                  <Button
                    variant="default"
                    onClick={handleNext}
                    iconName="ArrowRight"
                    iconPosition="right"
                  >
                    Continue
                  </Button>
                ) : (
                  <Button
                    variant="default"
                    onClick={handleSubmit}
                    loading={loading}
                    iconName="CheckCircle"
                    iconPosition="left"
                  >
                    {loading ? 'Registering...' : 'Complete Registration'}
                  </Button>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CustomerRegistrationModal;